import clsx from 'clsx'
import type { PropsWithChildren } from 'react'

import { FvDate, FvNum } from '@fv/client-components'
import type { PropsWithClassName, ShipmentLocation } from '@fv/client-types'
import type { CondensedColumn } from '@fv/models'

import { useTypeNames } from '@/hooks/settings'
import {
  getCarrierDetails,
  getDestination,
  getOrigin,
  modeNames,
} from '@/utils/shipmentFuncs'

import { HasPermission } from '../auth/HasPermission'
import type { CellProps, ColumnRegistryEntry } from './columns'
import { ShipmentStatusIndicator } from './ShipmentStatusIndicator'

const dateFormat = 'MMM D, YYYY'

export const Th = ({
  children,
  className,
}: PropsWithChildren<PropsWithClassName>) => {
  return (
    <th className={clsx('whitespace-nowrap px-2 py-3 font-normal', className)}>
      {children}
      <div className="border-fv-gray-300 absolute bottom-0 left-0 w-full border-b" />
    </th>
  )
}

export const Td = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return <td className={clsx('p-2', className)}>{children}</td>
}

export const ShipmentLoc = ({ loc }: { loc?: ShipmentLocation }) => {
  if (!loc) return
  return (
    <div className="flex gap-1.5">
      <span>{loc.city}</span>
      <span>{loc.state}</span>
      <span>{loc.postalCode}</span>
    </div>
  )
}

// Individual cell components
const TrackingCell = ({ load }: CellProps) => (
  <Td>{load.tracking?.trackingNumber}</Td>
)

const StatusCell = ({ load }: CellProps) => (
  <Td className="whitespace-nowrap">
    <ShipmentStatusIndicator load={load} />
  </Td>
)

const CarrierCell = ({ load }: CellProps) => (
  <Td className="">{getCarrierDetails(load, false)}</Td>
)

const OriginCell = ({ load }: CellProps) => (
  <Td>
    <ShipmentLoc loc={getOrigin(load)} />
  </Td>
)

const OriginCompanyCell = ({ load }: CellProps) => (
  <Td>{getOrigin(load)?.company || '-'}</Td>
)

const PickupCell = ({ load }: CellProps) => (
  <Td>
    <FvDate val={load.pickupDate} format={dateFormat} />
  </Td>
)

const DestinationCell = ({ load }: CellProps) => (
  <Td>
    <ShipmentLoc loc={getDestination(load)} />
  </Td>
)

const DestinationCompanyCell = ({ load }: CellProps) => (
  <Td>{getDestination(load)?.company || '-'}</Td>
)

const DeliveryCell = ({ load }: CellProps) => (
  <Td>
    {load.estimatedDeliveryDate ? (
      <FvDate val={load.estimatedDeliveryDate} format={dateFormat} />
    ) : (
      '-'
    )}
  </Td>
)

const ModeCell = ({ load }: CellProps) => (
  <Td>{modeNames[load.equipment.mode]}</Td>
)

const PickupDateActualCell = ({ load }: CellProps) => (
  <Td>
    {load.tracking?.pickupDateActual ? (
      <FvDate val={load.tracking?.pickupDateActual} format={dateFormat} />
    ) : (
      '-'
    )}
  </Td>
)

const DeliveryDateActualCell = ({ load }: CellProps) => (
  <Td>
    {load.tracking?.deliveryDateActual ? (
      <FvDate val={load.tracking?.deliveryDateActual} format={dateFormat} />
    ) : (
      '-'
    )}
  </Td>
)

const EquipmentTypeCell = ({ load }: CellProps) => {
  const { equipmentName } = useTypeNames()
  return <Td>{equipmentName(load.equipment.type)}</Td>
}

const DirectionCell = ({ load }: CellProps) => <Td>{load.direction || '-'}</Td>

const QuotedByCell = ({ load }: CellProps) => (
  <Td>{load.createdBy?.name || '-'}</Td>
)

const BookedByCell = ({ load }: CellProps) => (
  <Td>{load.bookedBy?.name || '-'}</Td>
)

const InterlineCell = ({ load }: CellProps) => (
  <Td>{load.selectedQuote?.interline ? 'Yes' : 'No'}</Td>
)

const VendorCell = ({ load }: CellProps) => <Td>{load.vendor?.name || '-'}</Td>

const ArchivedCell = ({ load }: CellProps) => (
  <Td>{load.isArchived ? 'Yes' : 'No'}</Td>
)

const TrailerNumberCell = ({ load }: CellProps) => (
  <Td>{load.refNums?.find(r => r.type === 'trailer-num')?.value || '-'}</Td>
)

const ShipperNumCell = ({ load }: CellProps) => (
  <Td>
    {getOrigin(load)
      ?.refNums?.map(r => r.value)
      ?.join(' / ') || '-'}
  </Td>
)

const PickupConfirmationCell = ({ load }: CellProps) => (
  <Td>{load.pickup?.confirmationNumber || '-'}</Td>
)

const UnitCountCell = ({ load }: CellProps) => {
  const unitCount = load.items.reduce((acc, cur) => acc + cur.quantity, 0)
  return <Td>{unitCount || '-'}</Td>
}

const TotalPriceCell = ({ load }: CellProps) => (
  <Td>
    {load.selectedQuote?.amount ? (
      <HasPermission name="view-rates" fallback={'-'}>
        <FvNum
          currency={load.selectedQuote?.currency}
          val={load.selectedQuote?.amount}
        />
      </HasPermission>
    ) : (
      '-'
    )}
  </Td>
)

export const columnsRegistry: Record<CondensedColumn, ColumnRegistryEntry> = {
  tracking: { label: 'Tracking#', Cell: TrackingCell },
  status: {
    label: 'Status',
    sortable: true,
    sortBy: 'status',
    Cell: StatusCell,
  },
  carrier: {
    label: 'Carrier',
    sortable: true,
    sortBy: 'carrier',
    Cell: CarrierCell,
  },
  origin: { label: 'Origin', Cell: OriginCell },
  'origin-company': { label: 'Shipper', Cell: OriginCompanyCell },
  pickup: {
    label: 'Pickup',
    sortable: true,
    sortBy: 'pickupDate',
    Cell: PickupCell,
  },
  destination: { label: 'Destination', Cell: DestinationCell },
  'destination-company': { label: 'Consignee', Cell: DestinationCompanyCell },
  delivery: {
    label: 'Deliver',
    sortable: true,
    sortBy: 'deliveryDate',
    Cell: DeliveryCell,
  },
  mode: { label: 'Mode', Cell: ModeCell },
  'pickup-date-actual': {
    label: 'Pickup date actual',
    Cell: PickupDateActualCell,
  },
  'delivery-date-actual': {
    label: 'Delivery date actual',
    Cell: DeliveryDateActualCell,
  },
  'equipment-type': { label: 'Equipment type', Cell: EquipmentTypeCell },
  direction: { label: 'Direction', Cell: DirectionCell },
  'quoted-by': { label: 'Quoted by', Cell: QuotedByCell },
  'booked-by': { label: 'Booked by', Cell: BookedByCell },
  interline: { label: 'Interline', Cell: InterlineCell },
  vendor: { label: 'Vendor', Cell: VendorCell },
  archived: { label: 'Archived', Cell: ArchivedCell },
  'trailer-number': { label: 'Trailer number', Cell: TrailerNumberCell },
  'pickup-confirmation': {
    label: 'Pickup Confirmation#',
    Cell: PickupConfirmationCell,
  },
  'unit-count': { label: 'Units', Cell: UnitCountCell },
  'total-price': { label: 'Total price', Cell: TotalPriceCell },
  'shipper-num': { label: 'Shipper #', Cell: ShipperNumCell },
}
